import { z } from 'zod';

import { ComponentDefinition } from '../../types/ComponentDefinition';
import { ComponentPayloadValidator, ComponentValidator } from '../Component';
import { AbstractPort, Port } from '../fields/AbstractPort';

const hvacOptions = ['battery', 'solar'] as const;

export type HVACPortPurposeOption = (typeof hvacOptions)[number];

const hvacPortPurposeValidator = z.enum(hvacOptions).nullable().default(null);

export const HVACPortPurpose = {
    validator: hvacPortPurposeValidator,
    options: hvacOptions,
};

const port = AbstractPort.extend({
    AC: Port.AC.extend({
        controlMethods: Port.controlMethods.validator,
    }).default({}),
    DC: Port.DC.extend({
        controlMethods: Port.controlMethods.validator,
    }).default({}),
    capacitance: Port.capacitance.validator,
    reverseDiode: z.boolean().default(false),
    equivalentSeriesResistance: Port.equivalentSeriesResistance.validator,
    powerFlowDirection: Port.powerFlowDirection.literals.input,
    purpose: hvacPortPurposeValidator,
}).default({});

const HVACBase = z.object({
    type: z.literal('hvac').default('hvac'),
    electrical: z
        .object({
            ports: z.array(port).min(1).default([undefined]),
        })
        .default({}),
});

export const HVACValidator = ComponentValidator.merge(HVACBase);
export const HVACPayloadValidator = ComponentPayloadValidator.merge(HVACBase);

export const HVACDefinition: ComponentDefinition = {
    type: 'hvac',
    name: 'HVAC',
    plural: 'HVACs',
    indicator: 'HVAC',
    hasDynamicNumberOfPorts: true,
    interoperabilityDataModel: true,
    ports: {
        min: 1,
        max: Infinity,
        powerFlowDirections: ['input'],
        allowedControlMethods: ['constant-power', 'power-voltage'],
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: HVACValidator,
        payload: HVACPayloadValidator,
    },
};

export type HVAC = z.infer<typeof HVACValidator>;
export type HVACPayload = z.infer<typeof HVACPayloadValidator>;
